import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bell, Loader2, Save } from "lucide-react";
import { API, authHeaders } from "@/constants/roles";

const THRESHOLDS = [2, 3, 5, 10];

/**
 * Worker's own job-alert preferences. Per-account only — changing these never
 * affects anyone else. Defaults (alerts on, every new job, email + in-site on)
 * apply automatically to accounts that have never touched this card.
 */
export const NotificationPrefsCard = () => {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/my/notification-prefs`, { headers: authHeaders() })
      .then((r) => setPrefs(r.data))
      .catch(() => toast.error("Could not load notification settings."));
  }, []);

  const save = async (patch) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    setSaving(true);
    try {
      const r = await axios.put(`${API}/my/notification-prefs`, patch, { headers: authHeaders() });
      setPrefs(r.data);
      toast.success("Notification settings saved.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save settings.");
    } finally { setSaving(false); }
  };

  if (!prefs) {
    return <div className="rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6"><Loader2 className="mx-auto h-4 w-4 animate-spin text-[var(--pv-text-4)]" /></div>;
  }

  const Toggle = ({ testid, label, hint, value, onChange, disabled }) => (
    <div className={`flex items-center justify-between gap-4 rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-4 py-3 ${disabled ? "opacity-50" : ""}`}>
      <div>
        <p className="text-sm font-bold text-[var(--pv-text)]">{label}</p>
        <p className="mt-0.5 text-[11px] text-[var(--pv-text-4)]">{hint}</p>
      </div>
      <button
        data-testid={testid}
        onClick={() => !disabled && onChange(!value)}
        disabled={disabled || saving}
        aria-pressed={value}
        className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${value ? "bg-cyan-500" : "bg-[var(--pv-raised)] border border-[var(--pv-border)]"}`}
      >
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${value ? "left-[1.375rem]" : "left-0.5"}`} />
      </button>
    </div>
  );

  return (
    <div data-testid="notif-prefs-card" className="rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6">
      <div className="flex items-center gap-2.5">
        <Bell className="h-4.5 w-4.5 text-cyan-400" />
        <h2 className="font-display text-base font-bold text-[var(--pv-text)]">Notifications</h2>
        {saving && <Loader2 className="h-3.5 w-3.5 animate-spin text-[var(--pv-text-4)]" />}
      </div>
      <p className="mt-1.5 text-xs text-[var(--pv-text-4)]">
        Control how you're told about new work you can claim. These settings are yours alone.
      </p>

      <div className="mt-4 space-y-2.5">
        <Toggle testid="notif-master-toggle" label="New job alerts" hint="Notify me whenever new jobs I can claim become available" value={prefs.job_alerts} onChange={(v) => save({ job_alerts: v })} />

        <div className={`rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-4 py-3 ${!prefs.job_alerts ? "opacity-50" : ""}`}>
          <p className="text-sm font-bold text-[var(--pv-text)]">How often</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              data-testid="notif-mode-every"
              onClick={() => !prefs.job_alerts || save({ mode: "every" })}
              disabled={!prefs.job_alerts || saving}
              className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${prefs.mode === "every" ? "bg-cyan-500 text-white" : "border border-[var(--pv-border)] text-[var(--pv-text-3)] hover:border-cyan-500/30"}`}
            >
              Every new job
            </button>
            <button
              data-testid="notif-mode-threshold"
              onClick={() => !prefs.job_alerts || save({ mode: "threshold" })}
              disabled={!prefs.job_alerts || saving}
              className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${prefs.mode === "threshold" ? "bg-cyan-500 text-white" : "border border-[var(--pv-border)] text-[var(--pv-text-3)] hover:border-cyan-500/30"}`}
            >
              Only when multiple are waiting
            </button>
            {prefs.mode === "threshold" && (
              <select
                data-testid="notif-threshold-select"
                value={prefs.threshold}
                onChange={(e) => save({ threshold: Number(e.target.value) })}
                disabled={!prefs.job_alerts || saving}
                className="rounded-full border border-[var(--pv-border)] bg-[var(--pv-bg)] px-3 py-2 text-xs font-bold text-[var(--pv-text-2)] focus:border-cyan-400 focus:outline-none"
              >
                {THRESHOLDS.map((n) => <option key={n} value={n}>{n}+ jobs available</option>)}
              </select>
            )}
          </div>
          {prefs.mode === "threshold" && (
            <p className="mt-2 text-[11px] text-[var(--pv-text-4)]">You'll only be alerted once {prefs.threshold}+ jobs you can claim are available at the same time.</p>
          )}
        </div>

        <Toggle testid="notif-email-toggle" label="Email notifications" hint="Send the alert to your account email too" value={prefs.email} onChange={(v) => save({ email: v })} disabled={!prefs.job_alerts} />
        <Toggle testid="notif-insite-toggle" label="In-site notifications" hint="Show the alert in your dashboard bell" value={prefs.insite} onChange={(v) => save({ insite: v })} disabled={!prefs.job_alerts} />
      </div>
    </div>
  );
};
